import { computed, Injectable, Signal, signal, WritableSignal } from '@angular/core';

const OVERLAY_HIDE_DELAY_MS = 250;

/**
 * LoaderService - Global fullscreen loading overlay counter.
 *
 * Data services call {@link startLoading} / {@link stopLoading} around fetches and mutations.
 * Sequential requests share one overlay: hide is delayed so a process finishing just before
 * the next one starts does not flash a second spinner.
 *
 * Scope: Global (`providedIn: 'root'`).
 *
 * Usage: {@link LayoutComponent} binds `[proffeoSpinner]` to {@link loading}. Feature views may
 * {@link suppressOverlay} while they show a local spinner instead.
 */
@Injectable({
  providedIn: 'root'
})
export class LoaderService {
  private readonly loadingFlag: WritableSignal<boolean> = signal(false);
  private readonly overlaySuppressed: WritableSignal<boolean> = signal(false);
  private loaderInstances: number[] = [];
  private hideTimer: ReturnType<typeof setTimeout> | null = null;

  public readonly loading: Signal<boolean> = computed(() => this.loadingFlag() && !this.overlaySuppressed());

  public startLoading(): void {
    this.clearHideTimer();
    this.addInstance();
    this.loadingFlag.set(true);
  }

  public stopLoading(): void {
    this.removeInstance();
  }

  /**
   * Hides the global overlay even when the counter is active.
   * Use while a view already shows its own spinner.
   */
  public suppressOverlay(): void {
    this.overlaySuppressed.set(true);
  }

  /** Shows the global overlay again when the counter is active. */
  public resumeOverlay(): void {
    this.overlaySuppressed.set(false);
  }

  private addInstance(): void {
    this.loaderInstances.push(1);
  }

  private removeInstance(): void {
    this.loaderInstances.pop();
    if (this.loaderInstances.length === 0) {
      this.scheduleHide();
    }
  }

  private scheduleHide(): void {
    this.clearHideTimer();
    this.hideTimer = setTimeout(() => {
      this.hideTimer = null;
      if (this.loaderInstances.length === 0) {
        this.loadingFlag.set(false);
      }
    }, OVERLAY_HIDE_DELAY_MS);
  }

  private clearHideTimer(): void {
    if (this.hideTimer === null) {
      return;
    }
    clearTimeout(this.hideTimer);
    this.hideTimer = null;
  }
}
